import type { SizingsSearch } from "@/components/types";
import {
  Order_By,
  type PerformanceEvaluationsQueryVariables,
  type ScenariosQueryVariables,
  type SizingsWithScenarioIdsQueryVariables
} from "gql/api";
import { computed, ref, watch } from "vue";
import {
  sizingsWhereNotDeleted,
  sizingsByTextSearch,
  sizingsByOwnerName,
  sizingsByWellName,
  sizingsByCustomerName,
  sizingsByDateRange,
  sizingsByPumpingType
} from "@/models/sizings/predicates";

import {
  scenariosByCustomerName,
  scenariosByDateRange,
  scenariosByOwnerName,
  scenariosByTextSearch,
  scenariosByWellName,
  scenariosWhereNotDeleted,
  scenariosSizingsByTextSearch,
  scenariosByPumpingType
} from "@/models/scenarios/predicates";
import {
  perfEvalsWhereNotDeleted,
  perfevalsByTextSearch,
  perfevalsByWellName,
  perfevalsByCustomerName,
  perfevalsByDateRange,
  perfevalsScenariosSizingsByTitle,
  perfevalsByPumpingType
} from "@/models/performance_evaluations/predicates";
import { isNullOrUndefinedOrEmptyString } from "@/utils/utils";
import { useSettingsStore } from "@/store";
import { useDebounceFn } from "@vueuse/core";

type SizingsAdvancedSearchArgs = {
  sizingsSearch?: SizingsSearch;
  scenariosWhere?: ScenariosQueryVariables["where"];
  sizingsWhere?: SizingsWithScenarioIdsQueryVariables["where"];
  perfevalsWhere?: PerformanceEvaluationsQueryVariables["where"];
};

export const useSizingsAdvancedSearch = (
  defaults?: SizingsAdvancedSearchArgs
) => {
  const { flags } = useSettingsStore();

  const {
    sizingsSearch: defaultSearch,
    sizingsWhere: defaultSizingsWhere,
    scenariosWhere: defaultScenariosWhere,
    perfevalsWhere: defaultPerfevalsWhere
  } = defaults || {};

  if (defaultSearch?.dateRange?.length) {
    defaultSearch.dateRange = defaultSearch.dateRange
      .filter(d => !!d)
      .map(d => new Date(d));
  }

  const sizingsSearch = ref<SizingsSearch>(
    defaultSearch || {
      dateRange: [],
      globalSearchText: "",
      ownerSearchText: "",
      customerSearchText: "",
      wellSearchText: ""
    }
  );

  const sizingsWhereRouteParams = computed(() => {
    const {
      dateRange,
      globalSearchText,
      ownerSearchText,
      customerSearchText,
      wellSearchText
    } = sizingsSearch.value;

    const dateIsDefault = !dateRange || dateRange.length === 0;
    const globalIsDefault = isNullOrUndefinedOrEmptyString(globalSearchText);
    const ownerIsDefault = isNullOrUndefinedOrEmptyString(ownerSearchText);
    const customerIsDefault =
      isNullOrUndefinedOrEmptyString(customerSearchText);
    const wellIsDefault = isNullOrUndefinedOrEmptyString(wellSearchText);
    if (
      dateIsDefault &&
      globalIsDefault &&
      ownerIsDefault &&
      customerIsDefault &&
      wellIsDefault
    ) {
      return;
    }
    const where: Partial<typeof sizingsSearch.value> = {};
    if (!dateIsDefault) {
      where.dateRange = sizingsSearch.value.dateRange;
    }
    if (!globalIsDefault) {
      where.globalSearchText = sizingsSearch.value.globalSearchText;
    }
    if (!ownerIsDefault) {
      where.ownerSearchText = sizingsSearch.value.ownerSearchText;
    }
    if (!customerIsDefault) {
      where.customerSearchText = sizingsSearch.value.customerSearchText;
    }
    if (!wellIsDefault) {
      where.wellSearchText = sizingsSearch.value.wellSearchText;
    }
    return where;
  });

  const scenariosWhere = ref<ScenariosQueryVariables["where"]>({
    ...scenariosWhereNotDeleted(),
    ...(defaultScenariosWhere || {})
  });
  const updateScenariosWhere = () => {
    scenariosWhere.value = {
      ...scenariosWhere.value,
      _and: [
        scenariosByTextSearch(sizingsSearch.value.globalSearchText),
        scenariosByOwnerName(sizingsSearch.value.ownerSearchText),
        scenariosByWellName(sizingsSearch.value.wellSearchText),
        scenariosByCustomerName(sizingsSearch.value.customerSearchText),
        scenariosByDateRange(sizingsSearch.value.dateRange),
        scenariosByPumpingType(flags.value.displayHpsOnly ? "hps" : undefined)
      ]
    };
  };
  updateScenariosWhere();

  const perfevalsWhere = ref<PerformanceEvaluationsQueryVariables["where"]>({
    ...perfEvalsWhereNotDeleted(),
    ...(defaultPerfevalsWhere || {})
  });
  const updatePerfEvalsWhere = () => {
    perfevalsWhere.value = {
      ...perfevalsWhere.value,
      _and: [
        perfevalsByTextSearch(sizingsSearch.value.globalSearchText),
        perfevalsByWellName(sizingsSearch.value.wellSearchText),
        perfevalsByCustomerName(sizingsSearch.value.customerSearchText),
        perfevalsByDateRange(sizingsSearch.value.dateRange),
        perfevalsByPumpingType(flags.value.displayHpsOnly ? "hps" : undefined)
      ]
    };
  };
  updatePerfEvalsWhere();

  const sizingsWhere = ref<SizingsWithScenarioIdsQueryVariables["where"]>({
    ...sizingsWhereNotDeleted(),
    ...(defaultSizingsWhere || {})
  });

  const updateSizingsWhere = () => {
    sizingsWhere.value = {
      ...sizingsWhere.value,
      _and: [
        sizingsByTextSearch(sizingsSearch.value.globalSearchText),
        sizingsByOwnerName(sizingsSearch.value.ownerSearchText),
        sizingsByWellName(sizingsSearch.value.wellSearchText),
        sizingsByCustomerName(sizingsSearch.value.customerSearchText),
        sizingsByDateRange(sizingsSearch.value.dateRange),
        sizingsByPumpingType(flags.value.displayHpsOnly ? "hps" : undefined)
      ]
    };
  };
  updateSizingsWhere();

  watch(
    () => flags.value.displayHpsOnly,
    () => {
      updateSizingsWhere();
      updateScenariosWhere();
      updatePerfEvalsWhere();
    }
  );

  watch(
    sizingsSearch,
    () => {
      updateSizingsWhere();
      updateScenariosWhere();
      updatePerfEvalsWhere();
    },
    { deep: true }
  );

  const getSearchRouteQuery = () => {
    const query: {
      customer?: string;
      global?: string;
      owner?: string;
      well?: string;
      dateRange?: string;
    } = {};

    if (!sizingsWhereRouteParams.value) {
      return query;
    }
    if (sizingsWhereRouteParams.value.customerSearchText) {
      query.customer = sizingsWhereRouteParams.value.customerSearchText;
    }

    if (sizingsWhereRouteParams.value.globalSearchText) {
      query.global = sizingsWhereRouteParams.value.globalSearchText;
    }

    if (sizingsWhereRouteParams.value.ownerSearchText) {
      query.owner = sizingsWhereRouteParams.value.ownerSearchText;
    }

    if (sizingsWhereRouteParams.value.wellSearchText) {
      query.well = sizingsWhereRouteParams.value.wellSearchText;
    }

    if (
      sizingsWhereRouteParams.value.dateRange &&
      sizingsWhereRouteParams.value.dateRange.filter(d => !!d).length
    ) {
      query.dateRange = sizingsWhereRouteParams.value.dateRange
        .filter(d => !!d)
        .map(date => date.toISOString())
        .join(",");
    }
    return query;
  };

  return {
    getSearchRouteQuery,
    sizingsWhereRouteParams,
    sizingsSearch,
    sizingsWhere,
    scenariosWhere,
    perfevalsWhere
  };
};

export const useDeletedSizingsAdvancedSearch = (
  defaults?: SizingsAdvancedSearchArgs
) => {
  const { flags } = useSettingsStore();

  const {
    sizingsSearch: defaultSearch,
    sizingsWhere: defaultSizingsWhere,
    scenariosWhere: defaultScenariosWhere,
    perfevalsWhere: defaultPerfevalsWhere
  } = defaults || {};

  if (defaultSearch?.dateRange?.length) {
    defaultSearch.dateRange = defaultSearch.dateRange
      .filter(d => !!d)
      .map(d => new Date(d));
  }

  const sizingsSearch = ref<SizingsSearch>(
    defaultSearch || {
      dateRange: [],
      globalSearchText: "",
      ownerSearchText: "",
      customerSearchText: "",
      wellSearchText: ""
    }
  );

  const sizingsWhereRouteParams = computed(() => {
    const {
      dateRange,
      globalSearchText,
      ownerSearchText,
      customerSearchText,
      wellSearchText
    } = sizingsSearch.value;

    const dateIsDefault = !dateRange || dateRange.length === 0;
    const globalIsDefault = isNullOrUndefinedOrEmptyString(globalSearchText);
    const ownerIsDefault = isNullOrUndefinedOrEmptyString(ownerSearchText);
    const customerIsDefault =
      isNullOrUndefinedOrEmptyString(customerSearchText);
    const wellIsDefault = isNullOrUndefinedOrEmptyString(wellSearchText);
    if (
      dateIsDefault &&
      globalIsDefault &&
      ownerIsDefault &&
      customerIsDefault &&
      wellIsDefault
    ) {
      return;
    }
    const where: Partial<typeof sizingsSearch.value> = {};
    if (!dateIsDefault) {
      where.dateRange = sizingsSearch.value.dateRange;
    }
    if (!globalIsDefault) {
      where.globalSearchText = sizingsSearch.value.globalSearchText;
    }
    if (!ownerIsDefault) {
      where.ownerSearchText = sizingsSearch.value.ownerSearchText;
    }
    if (!customerIsDefault) {
      where.customerSearchText = sizingsSearch.value.customerSearchText;
    }
    if (!wellIsDefault) {
      where.wellSearchText = sizingsSearch.value.wellSearchText;
    }
    return where;
  });

  const defaultOrderBy = { journal_deleted_at: Order_By.Desc };

  const scenOrderBy = {
    journal_deleted_at: Order_By.DescNullsLast,
    sizing: { journal_deleted_at: Order_By.Desc }
  };

  const evalsOrderBy = {
    journal_deleted_at: Order_By.DescNullsLast,
    scenario: { journal_deleted_at: Order_By.Desc }
  };

  const scenariosWhere = ref<ScenariosQueryVariables["where"]>({
    ...(defaultScenariosWhere || {})
  });
  const updateScenariosWhere = () => {
    scenariosWhere.value = {
      ...scenariosWhere.value,
      _and: [
        scenariosSizingsByTextSearch(sizingsSearch.value.globalSearchText),
        scenariosByOwnerName(sizingsSearch.value.ownerSearchText),
        scenariosByWellName(sizingsSearch.value.wellSearchText),
        scenariosByCustomerName(sizingsSearch.value.customerSearchText),
        scenariosByDateRange(sizingsSearch.value.dateRange),
        scenariosByPumpingType(flags.value.displayHpsOnly ? "hps" : undefined)
      ]
    };
  };
  updateScenariosWhere();

  const perfevalsWhere = ref<PerformanceEvaluationsQueryVariables["where"]>({
    ...(defaultPerfevalsWhere || {})
  });
  const updatePerfEvalsWhere = () => {
    perfevalsWhere.value = {
      ...perfevalsWhere.value,
      _and: [
        perfevalsScenariosSizingsByTitle(sizingsSearch.value.globalSearchText),
        perfevalsByWellName(sizingsSearch.value.wellSearchText),
        perfevalsByCustomerName(sizingsSearch.value.customerSearchText),
        perfevalsByDateRange(sizingsSearch.value.dateRange),
        perfevalsByPumpingType(flags.value.displayHpsOnly ? "hps" : undefined)
      ]
    };
  };
  updatePerfEvalsWhere();

  const sizingsWhere = ref<SizingsWithScenarioIdsQueryVariables["where"]>({
    ...(defaultSizingsWhere || {})
  });

  const updateSizingsWhere = () => {
    sizingsWhere.value = {
      ...sizingsWhere.value,
      _and: [
        sizingsByTextSearch(sizingsSearch.value.globalSearchText),
        sizingsByOwnerName(sizingsSearch.value.ownerSearchText),
        sizingsByWellName(sizingsSearch.value.wellSearchText),
        sizingsByCustomerName(sizingsSearch.value.customerSearchText),
        sizingsByDateRange(sizingsSearch.value.dateRange),
        sizingsByPumpingType(flags.value.displayHpsOnly ? "hps" : undefined)
      ]
    };
  };
  updateSizingsWhere();

  const sizingsOrderBy = ref<SizingsWithScenarioIdsQueryVariables["order_by"]>([
    defaultOrderBy
  ]);
  const scenariosOrderBy = ref<ScenariosQueryVariables["order_by"]>([
    scenOrderBy
  ]);
  const perfevalsOrderBy = ref<
    PerformanceEvaluationsQueryVariables["order_by"]
  >([evalsOrderBy]);

  const debouncedUpdate = useDebounceFn(() => {
    updateSizingsWhere();
    updateScenariosWhere();
    updatePerfEvalsWhere();
  }, 1000);

  watch(
    () => flags.value.displayHpsOnly,
    () => {
      updateSizingsWhere();
      updateScenariosWhere();
      updatePerfEvalsWhere();
    }
  );

  watch(
    sizingsSearch,
    () => {
      debouncedUpdate();
    },
    { deep: true }
  );

  const getSearchRouteQuery = () => {
    const query: {
      customer?: string;
      global?: string;
      owner?: string;
      well?: string;
      dateRange?: string;
    } = {};

    if (!sizingsWhereRouteParams.value) {
      return query;
    }
    if (sizingsWhereRouteParams.value.customerSearchText) {
      query.customer = sizingsWhereRouteParams.value.customerSearchText;
    }

    if (sizingsWhereRouteParams.value.globalSearchText) {
      query.global = sizingsWhereRouteParams.value.globalSearchText;
    }

    if (sizingsWhereRouteParams.value.ownerSearchText) {
      query.owner = sizingsWhereRouteParams.value.ownerSearchText;
    }

    if (sizingsWhereRouteParams.value.wellSearchText) {
      query.well = sizingsWhereRouteParams.value.wellSearchText;
    }

    if (
      sizingsWhereRouteParams.value.dateRange &&
      sizingsWhereRouteParams.value.dateRange.filter(d => !!d).length
    ) {
      query.dateRange = sizingsWhereRouteParams.value.dateRange
        .filter(d => !!d)
        .map(date => date.toISOString())
        .join(",");
    }
    return query;
  };

  return {
    getSearchRouteQuery,
    sizingsWhereRouteParams,
    sizingsSearch,
    sizingsWhere,
    scenariosWhere,
    perfevalsWhere,
    sizingsOrderBy,
    scenariosOrderBy,
    perfevalsOrderBy
  };
};
